import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useGetJudgeCalibrationReportQuery } from './api/evalApi';

function formatPercent(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}

function StatCard({
  label,
  value,
  color,
  tooltip,
}: {
  label: string;
  value: string | number;
  color?: string;
  tooltip?: string;
}) {
  const labelElement = (
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
  );

  return (
    <Card variant="outlined" sx={{ flex: 1, minWidth: 140 }}>
      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
        {tooltip ? (
          <Tooltip title={tooltip} arrow>
            {labelElement}
          </Tooltip>
        ) : (
          labelElement
        )}
        <Typography variant="h4" sx={{ color, fontWeight: 600 }}>
          {value}
        </Typography>
      </CardContent>
    </Card>
  );
}

function StatCardSkeleton() {
  return (
    <Card variant="outlined" sx={{ flex: 1, minWidth: 140 }}>
      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
        <Skeleton width={80} height={16} />
        <Skeleton width={60} height={36} sx={{ mt: 0.5 }} />
      </CardContent>
    </Card>
  );
}

function TableRowSkeleton({ columns }: { columns: number }) {
  return (
    <TableRow>
      {Array.from({ length: columns }, (_, i) => (
        <TableCell key={i}>
          <Skeleton width={i === 0 ? 80 : 60} />
        </TableCell>
      ))}
    </TableRow>
  );
}

const zebraStyles = {
  '&:nth-of-type(odd)': { bgcolor: 'action.hover' },
  '&:hover': { bgcolor: 'action.selected' },
} as const;

export default function EvalCalibrationPage() {
  const [daysDraft, setDaysDraft] = useState('30');
  const [days, setDays] = useState(30);

  const { data, isLoading, isFetching, error, refetch } = useGetJudgeCalibrationReportQuery({
    days,
  });
  const report = data?.report;

  const applyDaysWindow = () => {
    const parsed = Number(daysDraft);
    if (Number.isFinite(parsed) && parsed > 0) {
      setDays(Math.floor(parsed));
    }
  };

  const showEmpty = !isLoading && !error && !report;

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
      {/* Header row: title + refresh */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>
          Judge Calibration
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={isFetching ? undefined : <RefreshIcon />}
          disabled={isFetching}
          onClick={() => refetch()}
        >
          {isFetching ? 'Loading...' : 'Refresh'}
        </Button>
      </Box>

      {/* Days filter — compact inline form */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 3 }}>
        <TextField
          label="Window (days)"
          type="number"
          size="small"
          value={daysDraft}
          onChange={(event) => setDaysDraft(event.target.value)}
          inputProps={{ min: 1 }}
          sx={{ width: 150 }}
          helperText="How many days of review history to include"
        />
        <Button size="small" variant="contained" onClick={applyDaysWindow}>
          Apply
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load calibration report.
        </Alert>
      )}

      {/* Loading skeletons */}
      {isLoading && (
        <>
          <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
          </Stack>
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Skeleton width={120} height={24} sx={{ mb: 1 }} />
              <Skeleton width={200} />
            </CardContent>
          </Card>
          <Card variant="outlined">
            <CardContent>
              <Skeleton width={200} height={24} sx={{ mb: 2 }} />
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {['Provider', 'Model', 'Reviewed', 'Agreement', 'Disagreements'].map((h) => (
                        <TableCell key={h}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRowSkeleton columns={5} />
                    <TableRowSkeleton columns={5} />
                    <TableRowSkeleton columns={5} />
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </>
      )}

      {/* Empty state */}
      {showEmpty && (
        <Card variant="outlined" sx={{ textAlign: 'center', py: 6 }}>
          <CardContent>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No calibration data available
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Run some eval cases with judge assertions to see calibration metrics.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Data */}
      {report && (
        <>
          {/* Stat cards — 4-column grid */}
          <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
            <StatCard
              label="Reviewed Runs"
              value={report.reviewed_runs}
              tooltip="Runs where a human submitted a review verdict"
            />
            <StatCard
              label="Judge Assertions"
              value={report.judge_assertions}
              tooltip="Total AI-scored assertions evaluated in this time window"
            />
            <StatCard
              label="Agreement"
              value={formatPercent(report.agreement_rate)}
              color={report.agreement_rate >= 0.8 ? 'success.main' : undefined}
              tooltip="How often the AI judge and human reviewer agreed on the result"
            />
            <StatCard
              label="Disagreements"
              value={report.disagreement_count}
              color={report.disagreement_count > 0 ? 'error.main' : undefined}
              tooltip="Number of cases where the AI judge and human reviewer disagreed"
            />
          </Stack>

          {/* Global metrics section */}
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Global Metrics
              </Typography>
              <Stack direction="row" spacing={4}>
                <Box>
                  <Tooltip title="Percentage of assertions the AI judge marked as passing" arrow>
                    <Typography variant="caption" color="text.secondary">
                      Judge Pass Rate
                    </Typography>
                  </Tooltip>
                  <Typography variant="h6">{formatPercent(report.judge_pass_rate)}</Typography>
                </Box>
                <Box>
                  <Tooltip title="Percentage of runs that humans marked as passing" arrow>
                    <Typography variant="caption" color="text.secondary">
                      Human Pass Rate
                    </Typography>
                  </Tooltip>
                  <Typography variant="h6">{formatPercent(report.human_pass_rate)}</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          {/* Provider & model breakdown table */}
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                Provider and Model Breakdown
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <Tooltip title="The AI vendor (e.g. Anthropic, OpenAI)" arrow>
                          <span>Provider</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>Model</TableCell>
                      <TableCell align="right">Reviewed</TableCell>
                      <TableCell align="right">
                        <Tooltip
                          title="Agreement rate between AI judge and human for this model"
                          arrow
                        >
                          <span>Agreement</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Number of verdict conflicts for this model" arrow>
                          <span>Disagreements</span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {report.by_provider_model.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} sx={{ textAlign: 'center', py: 3 }}>
                          <Typography variant="body2" color="text.secondary">
                            No reviewed judge runs in this window.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      report.by_provider_model.map((summary) => (
                        <TableRow key={`${summary.provider}:${summary.model}`} sx={zebraStyles}>
                          <TableCell>{summary.provider}</TableCell>
                          <TableCell>{summary.model}</TableCell>
                          <TableCell align="right">{summary.reviewed_runs}</TableCell>
                          <TableCell align="right">
                            {formatPercent(summary.agreement_rate)}
                          </TableCell>
                          <TableCell align="right">{summary.disagreement_count}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
}
